import moment from 'moment';
import { Component, OnInit, signal } from '@angular/core';
import { IMPORTS } from './_imports';
import { NgxSpinnerService } from 'ngx-spinner';
import { DashboardService } from '../../../services/dashboard.service';
import { ToastrService } from 'ngx-toastr';
import { Enums } from '../../../shared/enums';
import { Utils } from '../../../shared/utils';
import { FinishedFlowInstancesCsvDescription, FinishedFlowInstances, CustomDateTimeCriteriaType, SearchCriteriaType, SearchToken, FinishedFlowInstancesDescription } from '../../../models/dashboard.model';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';

@Component({
    selector: 'finished-flow-instances',
    templateUrl: './finished-flow-instances.component.html',
    styleUrl: './finished-flow-instances.component.scss',
    standalone: true,
    imports: IMPORTS,
})
export class FinishedFlowInstancesComponent implements OnInit {
    // #region [Type props]
    CustomDateTimeCriteriaType = CustomDateTimeCriteriaType;
    FinishedFlowInstancesDescription = FinishedFlowInstancesDescription;
    // #endregion

    // #region [props]
    searchTokens: SearchToken[] = [];
    customFilterSearch: string = null;
    finishDateTypes: SearchCriteriaType[] = [
        SearchCriteriaType.FinishDateFrom,
        SearchCriteriaType.FinishDateUntil
    ];
    // #endregion

    // #region [signals]
    $results = signal(null as FinishedFlowInstances);
    $showResults = signal(false);
    $topPatriarchs = signal([] as any[]);
    $topPatriarchsSemGoves = signal([] as any[]);
    $topOrganizations = signal([] as any[]);
    $topOrganizationsSemGoves = signal([] as any[]);
    $topFlowDefinitions = signal([] as any[]);
    $topFlowDefinitionsSemGoves = signal([] as any[]);
    $selectedFromDateTime = signal(null as any);
    $selectedUntilDateTime = signal(null as any);
    $isShowingFinishDateSection = signal(true);
    // #endregion

    constructor(
        private spinner: NgxSpinnerService,
        private toastr: ToastrService,
        private dashboardService: DashboardService,
    ) { }

    ngOnInit() { }

    async getResults() {
        this.spinner.show();

        this.$isShowingFinishDateSection.set(false);

        const response = await this.dashboardService.getResultsFinishedFlowInstances({
            fromDateTime: this.getFromDateTime(),
            untilDateTime: this.getUntilDateTime()
        });

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        Utils.setSignal(this.$results, response.data);
        Utils.setSignal(this.$topPatriarchs, response.data.topPatriarchs.map(x => ({ name: x.pivot.split(' - ')[0], value: x.count })));
        Utils.setSignal(this.$topPatriarchsSemGoves, response.data.topPatriarchsSemGoves.map(x => ({ name: x.pivot.split(' - ')[0], value: x.count })));
        Utils.setSignal(this.$topOrganizations, response.data.topOrganizations.map(x => ({ name: x.pivot.split(' || (')[0].split(' - ')[0] + ' (' + x.pivot.split(' || (')[1].split(' - ')[0] + ')', value: x.count })));
        Utils.setSignal(this.$topOrganizationsSemGoves, response.data.topOrganizationsSemGoves.map(x => ({ name: x.pivot.split(' || (')[0].split(' - ')[0] + ' (' + x.pivot.split(' || (')[1].split(' - ')[0] + ')', value: x.count })));
        Utils.setSignal(this.$topFlowDefinitions, response.data.topFlowDefinitions.map(x => ({
            name: '{0} ({1} - {2} - {3})'
                .replace('{0}', JSON.parse(x.pivot).name)
                .replace('{1}', JSON.parse(x.pivot).patriarchInfo.split(' - ')[0])
                .replace('{2}', JSON.parse(x.pivot).organizationInfo.split(' - ')[0])
                .replace('{3}', JSON.parse(x.pivot).unitInfo.split(' - ')[0])
            ,
            value: x.count
        })));
        Utils.setSignal(this.$topFlowDefinitionsSemGoves, response.data.topFlowDefinitionsSemGoves.map(x => ({
            name: '{0} ({1} - {2} - {3})'
                .replace('{0}', JSON.parse(x.pivot).name)
                .replace('{1}', JSON.parse(x.pivot).patriarchInfo.split(' - ')[0])
                .replace('{2}', JSON.parse(x.pivot).organizationInfo.split(' - ')[0])
                .replace('{3}', JSON.parse(x.pivot).unitInfo.split(' - ')[0])
            ,
            value: x.count
        })));

        setTimeout(() => {
            this.$showResults.set(true);
            this.spinner.hide();
        }, 500);
    }

    async exportCsv() {
        this.spinner.show();

        try {
            const response = await this.dashboardService.exportCsvFinishedFlowInstances({
                fromDateTime: this.getFromDateTime(),
                untilDateTime: this.getUntilDateTime()
            });

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                this.spinner.hide();
                return;
            }

            if (response.data.length == 0) {
                this.toastr.warning(Enums.Messages.CsvNoRecordsError, Enums.Messages.Warning, Utils.getToastrErrorOptions());
                return;
            }

            const headers = Object.keys(response.data[0]).map(x => FinishedFlowInstancesCsvDescription.get(x));
            const splittedTime = (new Date()).toLocaleString().split(', ');
            const timeString = splittedTime[0].split('/').reverse().join('') + '_' + splittedTime[1].replaceAll(':', '');
            const filename = `E-Flow Instâncias de Fluxo executadas (${timeString})`;
            const csvOptions = {
                fieldSeparator: ';',
                showLabels: true,
                headers: [...new Set(headers)],
                nullToEmptyString: true
            };

            new AngularCsv(response.data, filename, csvOptions);

            this.toastr.success(Enums.Messages.CsvGenerationSuccess, Enums.Messages.Success);
        } catch (error) {
            this.toastr.error(Enums.Messages.CsvExportError, Enums.Messages.Error, Utils.getToastrErrorOptions());
            this.spinner.hide();
            console.error(error);
        } finally {
            this.spinner.hide();
        }
    }

    toggleFinishDateSection() {
        this.$isShowingFinishDateSection.set(!this.$isShowingFinishDateSection());
    }

    addCustomFinishDateFilterCriteria(type: CustomDateTimeCriteriaType) {
        this.searchTokens = this.searchTokens.filter(x =>
            ![
                SearchCriteriaType.FinishDateFrom,
                SearchCriteriaType.FinishDateUntil
            ].includes(x.searchCriteriaType)
        );

        let now = moment();
        let from = moment(now);
        let until = moment(now);

        switch (type) {
            case CustomDateTimeCriteriaType.Today:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').utc().set({ h: 0, m: 0, s: 0, ms: 0 }).toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.Last24:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-24, 'h').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.Last48:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-48, 'h').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.Last72:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-72, 'h').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.LastWeek:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-1, 'w').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.LastFortnight:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-15, 'd').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.LastMonth:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-1, 'M').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            case CustomDateTimeCriteriaType.LastYear:
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                    key: from.add(from.utcOffset(), 'm').add(-1, 'y').utc().toISOString(),
                    text: `De: ${from.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                this.searchTokens.push(new SearchToken({
                    searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                    key: until.add(until.utcOffset(), 'm').utc().toISOString(),
                    text: `Até: ${until.format('DD/MM/YYYY, HH:mm:ss')}`
                }));
                break;

            default:
                break;
        }

        this.searchTokens = [...new Set(this.searchTokens)];
    }

    addPredefinedFinishDateFilterCriteria() {
        if (this.$selectedFromDateTime() == null && this.$selectedUntilDateTime() == null) return;

        this.searchTokens = this.searchTokens.filter(x =>
            ![
                SearchCriteriaType.FinishDateFrom,
                SearchCriteriaType.FinishDateUntil
            ].includes(x.searchCriteriaType)
        );

        if (this.$selectedFromDateTime() != null) {
            this.searchTokens.push(new SearchToken({
                searchCriteriaType: SearchCriteriaType.FinishDateFrom,
                key: this.$selectedFromDateTime(),
                text: `De: ${moment.utc(this.$selectedFromDateTime()).format('DD/MM/YYYY, HH:mm:ss')}`
            }));
        }

        if (this.$selectedUntilDateTime() != null) {
            this.searchTokens.push(new SearchToken({
                searchCriteriaType: SearchCriteriaType.FinishDateUntil,
                key: this.$selectedUntilDateTime(),
                text: `Até: ${moment.utc(this.$selectedUntilDateTime()).format('DD/MM/YYYY, HH:mm:ss')}`
            }));
        }

        this.searchTokens = [...new Set(this.searchTokens)];
        this.clearFinishDateFilterCriteria();
    }

    clearFinishDateFilterCriteria() {
        this.$selectedFromDateTime.set(null);
        this.$selectedUntilDateTime.set(null);
    }

    getSearchTokensFinishDate(): SearchToken[] {
        return this.searchTokens.filter(x => this.finishDateTypes.includes(x.searchCriteriaType));
    }

    removeFilter(searchToken: SearchToken) {
        if (this.finishDateTypes.includes(searchToken.searchCriteriaType)) {
            this.searchTokens = this.searchTokens.filter(x => !this.finishDateTypes.includes(x.searchCriteriaType));
        } else {
            this.searchTokens = this.searchTokens.filter(x => x != searchToken);
        }
    }

    shouldDisableSearch(): boolean {
        return this.searchTokens.length == 0;
    }

    // #region [===== private methods =====]
    // #endregion

    private getFromDateTime(): string {
        return ((this.searchTokens.find(x => x.searchCriteriaType == SearchCriteriaType.FinishDateFrom)?.key || moment({ y: 2000, M: 6, d: 1 }).utc().toISOString())
            .split('.')[0] + 'Z').replaceAll('ZZ', 'Z');
    }

    private getUntilDateTime(): string {
        return ((this.searchTokens.find(x => x.searchCriteriaType == SearchCriteriaType.FinishDateUntil)?.key || moment({ y: 3000, M: 6, d: 1 }).utc().toISOString())
            .split('.')[0] + 'Z').replaceAll('ZZ', 'Z');
    }
}
